export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  const text = await res.text();
  return text.length > 0 ? text : null;
}

/** Pluggable getter for the active mailbox id, injected by MailboxProvider. */
let _getActiveMailboxId: (() => string | null) | null = null;

export function setActiveMailboxIdGetter(fn: () => string | null) {
  _getActiveMailboxId = fn;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const mailboxId = _getActiveMailboxId?.() ?? null;

  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(mailboxId ? { "X-Mailbox-Id": mailboxId } : {}),
      ...(init.headers ?? {}),
    },
  });

  const body = await parseBody(res);

  if (!res.ok) {
    throw new ApiError(res.status, body);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, init?: RequestInit): Promise<T> =>
    request<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, data?: unknown, init?: RequestInit): Promise<T> =>
    request<T>(path, {
      ...init,
      method: "POST",
      body: data !== undefined ? JSON.stringify(data) : null,
    }),
  patch: <T>(path: string, data?: unknown, init?: RequestInit): Promise<T> =>
    request<T>(path, {
      ...init,
      method: "PATCH",
      body: data !== undefined ? JSON.stringify(data) : null,
    }),
  delete: <T>(path: string, init?: RequestInit): Promise<T> =>
    request<T>(path, { ...init, method: "DELETE" }),
};
